/**
 * Collection KB Manager Modal
 *
 * Provides a modal interface for managing which KBs are assigned to collections.
 */

import { getAuthToken } from './auth.js';
import { escapeHtml } from './html-escape.js';
import { confirm } from './confirm-dialog.js';

// State
let collections = [];
let allKBs = [];
let selectedCollection = null;
let assignedKBs = [];
let currentView = 'list';  // 'list' | 'detail'
let modalOpen = false;

// DOM elements cache
const elements = {};

/**
 * Initialize the Collection KB Manager
 */
export function initCollectionKBManager() {
    createModalHTML();
    cacheElements();
    setupEventListeners();
}

/**
 * Create the modal HTML structure
 */
function createModalHTML() {
    const modalHTML = `
        <div id="collection-kb-manager-overlay" class="modal-overlay"></div>
        <div id="collection-kb-manager-panel" class="kb-manager-panel">
            <div class="kb-manager-header">
                <div class="kb-manager-nav">
                    <button id="collection-kb-back-btn" class="kb-back-btn hidden">← Back</button>
                    <h2 id="collection-kb-title">Manage Collections</h2>
                </div>
                <button id="collection-kb-close" class="modal-close">&times;</button>
            </div>

            <!-- List View -->
            <div id="collection-kb-list-view" class="kb-view">
                <div class="kb-manager-toolbar">
                    <input type="text" id="collection-kb-search" class="kb-search" placeholder="Search collections...">
                    <button id="create-collection-btn" class="kb-create-btn" title="New Collection">+</button>
                </div>
                <div id="collection-kb-list" class="kb-list"></div>
            </div>

            <!-- Detail View -->
            <div id="collection-kb-detail-view" class="kb-view hidden">
                <div class="kb-assignment-section">
                    <h3>Assigned KBs</h3>
                    <div id="collection-assigned-kbs" class="kb-assignment-list"></div>
                </div>
                <div class="kb-assignment-section">
                    <h3>Available KBs</h3>
                    <div id="collection-available-kbs" class="kb-assignment-list"></div>
                </div>
            </div>

            <!-- Create Collection Dialog -->
            <div id="collection-create-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content">
                    <h3>Create New Collection</h3>
                    <input type="text" id="new-collection-name" placeholder="Collection name (e.g., My Collection)">
                    <input type="text" id="new-collection-title" placeholder="Display title (optional)">
                    <input type="text" id="new-collection-description" placeholder="Description (optional)">
                    <div class="kb-dialog-actions">
                        <button id="collection-create-cancel" class="btn-secondary">Cancel</button>
                        <button id="collection-create-confirm" class="kb-create-btn">Create</button>
                    </div>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Cache DOM elements
 */
function cacheElements() {
    elements.overlay = document.getElementById('collection-kb-manager-overlay');
    elements.panel = document.getElementById('collection-kb-manager-panel');
    elements.closeBtn = document.getElementById('collection-kb-close');
    elements.backBtn = document.getElementById('collection-kb-back-btn');
    elements.title = document.getElementById('collection-kb-title');
    elements.searchInput = document.getElementById('collection-kb-search');
    elements.createBtn = document.getElementById('create-collection-btn');

    // Views
    elements.listView = document.getElementById('collection-kb-list-view');
    elements.detailView = document.getElementById('collection-kb-detail-view');
    elements.collectionList = document.getElementById('collection-kb-list');
    elements.assignedList = document.getElementById('collection-assigned-kbs');
    elements.availableList = document.getElementById('collection-available-kbs');

    // Create dialog
    elements.createDialog = document.getElementById('collection-create-dialog');
    elements.newCollectionName = document.getElementById('new-collection-name');
    elements.newCollectionTitle = document.getElementById('new-collection-title');
    elements.newCollectionDescription = document.getElementById('new-collection-description');
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    elements.overlay?.addEventListener('click', closeCollectionKBManager);
    elements.closeBtn?.addEventListener('click', closeCollectionKBManager);
    elements.backBtn?.addEventListener('click', showListView);
    elements.searchInput?.addEventListener('input', renderCollectionList);
    elements.createBtn?.addEventListener('click', showCreateDialog);

    // Create dialog
    document.getElementById('collection-create-cancel')?.addEventListener('click', hideCreateDialog);
    document.getElementById('collection-create-confirm')?.addEventListener('click', handleCreateCollection);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) {
            if (!elements.createDialog?.classList.contains('hidden')) {
                hideCreateDialog();
            } else if (currentView === 'detail') {
                showListView();
            } else {
                closeCollectionKBManager();
            }
        }
    });
}

/**
 * Open the Collection KB Manager modal
 */
export async function openCollectionKBManager() {
    modalOpen = true;
    selectedCollection = null;
    currentView = 'list';

    // Reset search
    if (elements.searchInput) {
        elements.searchInput.value = '';
    }

    // Show modal
    elements.overlay?.classList.add('active');
    elements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Fetch data and render
    await Promise.all([fetchCollections(), fetchAllKBs()]);
    showListView();
}

/**
 * Close the Collection KB Manager modal
 */
export function closeCollectionKBManager() {
    modalOpen = false;
    elements.overlay?.classList.remove('active');
    elements.panel?.classList.remove('open');
    document.body.style.overflow = '';
}

/**
 * Fetch all collections
 */
async function fetchCollections() {
    try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['X-Auth-Token'] = token;

        const response = await fetch('/collections', { headers });
        if (!response.ok) throw new Error('Failed to fetch collections');
        const data = await response.json();
        collections = data.collections || [];
    } catch (error) {
        console.error('Error fetching collections:', error);
        collections = [];
    }
}

/**
 * Fetch all available KBs
 */
async function fetchAllKBs() {
    try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['X-Auth-Token'] = token;

        const response = await fetch('/kb', { headers });
        if (!response.ok) throw new Error('Failed to fetch KBs');
        const data = await response.json();
        allKBs = data.kbs || [];
    } catch (error) {
        console.error('Error fetching KBs:', error);
        allKBs = [];
    }
}

/**
 * Show the list view
 */
function showListView() {
    currentView = 'list';
    selectedCollection = null;
    elements.listView?.classList.remove('hidden');
    elements.detailView?.classList.add('hidden');
    elements.backBtn?.classList.add('hidden');
    elements.title.textContent = 'Manage Collections';
    renderCollectionList();
}

/**
 * Show the detail view for a collection
 */
async function showDetailView(collectionName) {
    currentView = 'detail';
    selectedCollection = collectionName;

    elements.listView?.classList.add('hidden');
    elements.detailView?.classList.remove('hidden');
    elements.backBtn?.classList.remove('hidden');

    // Find collection for display
    const collection = collections.find(c => c.name === collectionName);
    elements.title.textContent = collection?.title || formatDisplayName(collectionName);

    await loadCollectionKBs();
}

/**
 * Load KBs for the selected collection
 */
async function loadCollectionKBs() {
    if (!selectedCollection) return;

    try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['X-Auth-Token'] = token;

        const response = await fetch(`/collections/${encodeURIComponent(selectedCollection)}/kbs`, { headers });
        if (!response.ok) throw new Error('Failed to fetch collection KBs');
        const data = await response.json();
        assignedKBs = data.kbs || [];
    } catch (error) {
        console.error('Error fetching collection KBs:', error);
        assignedKBs = [];
    }

    renderAssignmentLists();
}

/**
 * Render the collection list
 */
function renderCollectionList() {
    if (!elements.collectionList) return;

    const searchTerm = elements.searchInput?.value.toLowerCase() || '';

    const filteredCollections = collections.filter(collection => {
        const name = collection.name?.toLowerCase() || '';
        const title = collection.title?.toLowerCase() || '';
        const desc = collection.description?.toLowerCase() || '';
        return name.includes(searchTerm) || title.includes(searchTerm) || desc.includes(searchTerm);
    });

    if (filteredCollections.length === 0) {
        elements.collectionList.innerHTML = `
            <div class="kb-empty">
                ${collections.length === 0 ? 'No collections available' : 'No collections match your search'}
            </div>
        `;
        return;
    }

    let html = '';
    for (const collection of filteredCollections) {
        const displayName = collection.title || formatDisplayName(collection.name);
        html += `
            <div class="kb-card" data-collection-name="${escapeHtml(collection.name)}">
                <div class="kb-card-info">
                    <span class="kb-card-name">${escapeHtml(displayName)}</span>
                    <span class="kb-card-meta">${collection.kb_count || 0} KBs</span>
                </div>
                <div class="kb-card-actions">
                    <button class="kb-action-btn delete-collection-btn" data-collection="${escapeHtml(collection.name)}" title="Delete Collection">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;
    }

    elements.collectionList.innerHTML = html;

    // Add click handlers
    elements.collectionList.querySelectorAll('.kb-card').forEach(card => {
        card.addEventListener('click', (e) => {
            // Don't trigger if clicking delete button
            if (e.target.closest('.delete-collection-btn')) return;
            const collectionName = card.dataset.collectionName;
            showDetailView(collectionName);
        });
    });

    // Add delete handlers
    elements.collectionList.querySelectorAll('.delete-collection-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const collectionName = btn.dataset.collection;
            const confirmed = await confirm({
                title: 'Delete Collection',
                message: `Delete collection "${collectionName}"? This cannot be undone.`,
                confirmText: 'Delete',
                danger: true
            });
            if (confirmed) {
                await deleteCollection(collectionName);
            }
        });
    });
}

/**
 * Render assignment lists (assigned and available KBs)
 */
function renderAssignmentLists() {
    // Assigned KBs
    if (elements.assignedList) {
        if (assignedKBs.length === 0) {
            elements.assignedList.innerHTML = '<div class="kb-empty">No KBs assigned</div>';
        } else {
            let html = '';
            for (const kbName of assignedKBs) {
                html += `
                    <div class="kb-assignment-item">
                        <span class="kb-assignment-name">${escapeHtml(kbName)}</span>
                        <button class="unassign-btn" data-kb="${escapeHtml(kbName)}">Remove</button>
                    </div>
                `;
            }
            elements.assignedList.innerHTML = html;

            // Add unassign handlers
            elements.assignedList.querySelectorAll('.unassign-btn').forEach(btn => {
                btn.addEventListener('click', () => unassignKB(btn.dataset.kb));
            });
        }
    }

    // Available KBs (not assigned)
    if (elements.availableList) {
        const availableKBs = allKBs.filter(kb => !assignedKBs.includes(kb.name));

        if (availableKBs.length === 0) {
            elements.availableList.innerHTML = '<div class="kb-empty">All KBs are assigned</div>';
        } else {
            let html = '';
            for (const kb of availableKBs) {
                html += `
                    <div class="kb-assignment-item">
                        <span class="kb-assignment-name">${escapeHtml(kb.name)}</span>
                        <button class="assign-btn" data-kb="${escapeHtml(kb.name)}">Add</button>
                    </div>
                `;
            }
            elements.availableList.innerHTML = html;

            // Add assign handlers
            elements.availableList.querySelectorAll('.assign-btn').forEach(btn => {
                btn.addEventListener('click', () => assignKB(btn.dataset.kb));
            });
        }
    }
}

/**
 * Assign a KB to the selected collection
 */
async function assignKB(kbName) {
    if (!selectedCollection) return;

    try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['X-Auth-Token'] = token;

        const response = await fetch(
            `/collections/${encodeURIComponent(selectedCollection)}/kbs?kb_name=${encodeURIComponent(kbName)}`,
            { method: 'POST', headers }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to assign KB');
        }

        // Refresh the lists
        await loadCollectionKBs();
        // Update collection count in list
        const collection = collections.find(c => c.name === selectedCollection);
        if (collection) collection.kb_count = assignedKBs.length;
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Unassign a KB from the selected collection
 */
async function unassignKB(kbName) {
    if (!selectedCollection) return;

    try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['X-Auth-Token'] = token;

        const response = await fetch(
            `/collections/${encodeURIComponent(selectedCollection)}/kbs/${encodeURIComponent(kbName)}`,
            { method: 'DELETE', headers }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to unassign KB');
        }

        // Refresh the lists
        await loadCollectionKBs();
        // Update collection count in list
        const collection = collections.find(c => c.name === selectedCollection);
        if (collection) collection.kb_count = assignedKBs.length;
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Show create collection dialog
 */
function showCreateDialog() {
    elements.createDialog?.classList.remove('hidden');
    elements.newCollectionName.value = '';
    elements.newCollectionTitle.value = '';
    elements.newCollectionDescription.value = '';
    elements.newCollectionName?.focus();
}

/**
 * Hide create collection dialog
 */
function hideCreateDialog() {
    elements.createDialog?.classList.add('hidden');
}

/**
 * Handle create collection
 */
async function handleCreateCollection() {
    const name = elements.newCollectionName?.value.trim();
    const title = elements.newCollectionTitle?.value.trim();
    const description = elements.newCollectionDescription?.value.trim();

    if (!name) {
        alert('Collection name is required');
        return;
    }

    try {
        const headers = { 'Content-Type': 'application/json' };
        const token = getAuthToken();
        if (token) headers['X-Auth-Token'] = token;

        const response = await fetch('/collections', {
            method: 'POST',
            headers,
            body: JSON.stringify({
                name,
                title: title || null,
                description: description || null
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to create collection');
        }

        hideCreateDialog();
        await fetchCollections();
        renderCollectionList();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Delete a collection
 */
async function deleteCollection(collectionName) {
    try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['X-Auth-Token'] = token;

        const response = await fetch(`/collections/${encodeURIComponent(collectionName)}`, {
            method: 'DELETE',
            headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete collection');
        }

        await fetchCollections();
        renderCollectionList();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Format display name (convert kebab-case to Title Case)
 */
function formatDisplayName(name) {
    if (!name) return '';
    return name
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Escape HTML to prevent XSS
 */

